// NotificationContext.js - React context for notification state management

import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { useToast } from '../hooks/ToastContext';
import { useTranslation } from 'react-i18next';
import { 
  shouldShowToast, 
  createToastFromNotification,
  NOTIFICATION_CATEGORIES 
} from './NotificationUtils';

// Initial state
const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  isDropdownOpen: false,
  lastFetchTime: null
};

// Action types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  APPEND_NOTIFICATIONS: 'APPEND_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  SET_UNREAD_COUNT: 'SET_UNREAD_COUNT',
  SET_DROPDOWN_OPEN: 'SET_DROPDOWN_OPEN',
  MARK_AS_READ: 'MARK_AS_READ',
  MARK_ALL_AS_READ: 'MARK_ALL_AS_READ',
  SET_LAST_FETCH_TIME: 'SET_LAST_FETCH_TIME'
};

// Reducer function
const notificationReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return { ...state, isLoading: action.payload };
      
    case ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
      
    case ACTION_TYPES.SET_NOTIFICATIONS:
      return { 
        ...state, 
        notifications: action.payload, 
        isLoading: false,
        error: null 
      };
      
    case ACTION_TYPES.APPEND_NOTIFICATIONS:
      // Append notifications for pagination, avoiding duplicates
      const existingIds = new Set(state.notifications.map(n => n.id));
      const newNotifications = action.payload.filter(n => !existingIds.has(n.id));
      return {
        ...state,
        notifications: [...state.notifications, ...newNotifications],
        isLoading: false,
        error: null
      };
      
    case ACTION_TYPES.ADD_NOTIFICATION:
      // Add new notification to the beginning of the list
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: action.payload.is_read ? state.unreadCount : state.unreadCount + 1
      };
      
    case ACTION_TYPES.UPDATE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.map(notif =>
          notif.id === action.payload.id ? { ...notif, ...action.payload } : notif
        )
      };
      
    case ACTION_TYPES.REMOVE_NOTIFICATION:
      const removedNotification = state.notifications.find(n => n.id === action.payload);
      return {
        ...state,
        notifications: state.notifications.filter(notif => notif.id !== action.payload),
        unreadCount: removedNotification && !removedNotification.is_read 
          ? Math.max(0, state.unreadCount - 1) 
          : state.unreadCount
      };
      
    case ACTION_TYPES.SET_UNREAD_COUNT:
      return { ...state, unreadCount: action.payload };
      
    case ACTION_TYPES.SET_DROPDOWN_OPEN:
      return { ...state, isDropdownOpen: action.payload };
      
    case ACTION_TYPES.MARK_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map(notif =>
          notif.id === action.payload 
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };
      
    case ACTION_TYPES.MARK_ALL_AS_READ:
      return {
        ...state,
        notifications: state.notifications.map(notif => ({
          ...notif,
          is_read: true,
          read_at: new Date().toISOString()
        })),
        unreadCount: 0
      };
      
    case ACTION_TYPES.SET_LAST_FETCH_TIME:
      return { ...state, lastFetchTime: action.payload };
      
    default:
      return state;
  }
};

// Create context
const NotificationContext = createContext();

// Custom hook to use notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { user } = useContext(AuthContext);  // Use same pattern as NotificationDropdown
  const { addToast } = useToast();
  const { t, i18n } = useTranslation('common');
  const fetchTimeoutRef = useRef(null);
  const lastFetchRef = useRef(0);
  const isInitializedRef = useRef(false);

  // Debug log the user context (reduce spam)
  // console.log('NotificationProvider - AuthContext:', { 
  //   user, 
  //   userId: user?.id,
  //   userExists: !!user 
  // });

  // Debounced fetch to prevent spam
  const debouncedFetch = useCallback((limit = 10, offset = 0, category = null) => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchRef.current;
    
    // Prevent fetching more than once per second
    if (timeSinceLastFetch < 1000) {
      console.log('fetchNotifications: Skipping fetch - too soon since last fetch');
      return;
    }
    
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Set a short delay to batch multiple calls
    fetchTimeoutRef.current = setTimeout(() => {
      fetchNotificationsInternal(limit, offset, category);
    }, 100);
  }, []);

  // Internal fetch function
  const fetchNotificationsInternal = useCallback(async (limit = 10, offset = 0, category = null) => {
    console.log('=== FETCH NOTIFICATIONS START ===');
    // console.log('fetchNotificationsInternal called with:', { 
    //   user: !!user, 
    //   userId: user?.id, 
    //   userType: typeof user,
    //   userKeys: user ? Object.keys(user) : [],
    //   limit, 
    //   offset, 
    //   category 
    // });
    
    if (!user?.id) {
      console.log('❌ fetchNotifications: No user ID available - user:', user);
      console.log('=== FETCH NOTIFICATIONS END (NO USER) ===');
      return;
    }

    try {
      lastFetchRef.current = Date.now();
      console.log('✅ fetchNotifications: Fetching notifications for user:', user.id);
      
      // Only show loading on initial fetch or when offset is 0
      if (offset === 0) {
        dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
      }

      // Use RPC function
      console.log('📡 Calling Supabase RPC function...');
      const { data, error } = await supabase.rpc('get_user_notifications', {
        target_user_id: user.id,
        limit_count: limit,
        offset_count: offset,
        category_filter: category,
        unread_only: false
      });

      if (error) {
        console.error('❌ fetchNotifications RPC error:', error);
        throw error;
      }

      console.log('✅ fetchNotifications: Received', data?.length || 0, 'notifications');
      console.log('📋 fetchNotifications: Data sample:', data?.slice(0, 2));

      if (offset === 0) {
        // Fresh fetch - replace all notifications
        console.log('🔄 Replacing notifications in state');
        dispatch({ type: ACTION_TYPES.SET_NOTIFICATIONS, payload: data || [] });
      } else {
        // Pagination - append to existing notifications
        console.log('➕ Appending notifications to state');
        dispatch({ type: ACTION_TYPES.APPEND_NOTIFICATIONS, payload: data || [] });
      }

      dispatch({ type: ACTION_TYPES.SET_LAST_FETCH_TIME, payload: new Date() });
      console.log('=== FETCH NOTIFICATIONS END (SUCCESS) ===');
      
      // Return the fetched data so caller can check if there are more
      return data || [];
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error.message });
      console.log('=== FETCH NOTIFICATIONS END (ERROR) ===');
      return [];
    }
  }, [user?.id]);  // Removed state.notifications dependency to prevent loops

  // Public fetch function with debouncing
  const fetchNotifications = useCallback(async (limit = 10, offset = 0, category = null) => {
    if (offset === 0) {
      // For initial load, use debouncing
      debouncedFetch(limit, offset, category);
      return Promise.resolve([]);
    } else {
      // For pagination, call directly and return result
      return await fetchNotificationsInternal(limit, offset, category);
    }
  }, [debouncedFetch, fetchNotificationsInternal]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) {
      console.log('fetchUnreadCount: No user ID available');
      return;
    }

    try {
      console.log('fetchUnreadCount: Fetching for user:', user.id);
      const { data, error } = await supabase.rpc('get_unread_notification_count', {
        target_user_id: user.id
      });

      if (error) {
        console.error('fetchUnreadCount error:', error);
        throw error;
      }

      console.log('fetchUnreadCount: Received count:', data);
      dispatch({ type: ACTION_TYPES.SET_UNREAD_COUNT, payload: data || 0 });
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user?.id]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase.rpc('mark_notification_read', {
        notification_id: notificationId,
        target_user_id: user.id
      });

      if (error) throw error;

      dispatch({ type: ACTION_TYPES.MARK_AS_READ, payload: notificationId });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [user?.id]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase.rpc('mark_all_notifications_read', {
        target_user_id: user.id
      });

      if (error) throw error;

      dispatch({ type: ACTION_TYPES.MARK_ALL_AS_READ });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [user?.id]);

  // Handle real-time notification updates
  const handleRealtimeNotification = useCallback((payload) => {
    console.log('Real-time notification received:', payload);
    
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    switch (eventType) {
      case 'INSERT':
        // New notification created
        if (newRecord.user_id === user?.id) {
          console.log('New notification for current user, ID:', newRecord.id);
          
          // Fetch the full processed notification and create toast
          // The ADD_NOTIFICATION action will handle incrementing unread count correctly
          // No need to fetchUnreadCount() here as it causes double increment
          handleNewNotification(newRecord.id, true); // Pass true to indicate we need toast
        }
        break;
        
      case 'UPDATE':
        // Notification updated (e.g., marked as read)
        if (newRecord.user_id === user?.id) {
          dispatch({ 
            type: ACTION_TYPES.UPDATE_NOTIFICATION, 
            payload: newRecord 
          });
        }
        break;
        
      case 'DELETE':
        // Notification deleted
        if (oldRecord.user_id === user?.id) {
          dispatch({ 
            type: ACTION_TYPES.REMOVE_NOTIFICATION, 
            payload: oldRecord.id 
          });
        }
        break;
        
      default:
        break;
    }
  }, [user?.id, addToast, t, i18n.language]);

  // Create toast from notification data (V2 - with proper translation)
  const createNotificationToast = useCallback((notificationData) => {
    console.log('🍞 Creating toast for notification (V2):', {
      id: notificationData.id,
      type: notificationData.type,
      message: notificationData.message,
      template: notificationData.message_template,
      params: notificationData.message_params,
      currentLang: i18n.language
    });
    
    // Map notification types to toast types
    const toastTypes = {
      'ride_request': 'ride_request',
      'ride_accepted': 'success',
      'ride_declined': 'error',
      'ride_cancelled': 'warning',
      'ride_completed': 'success',
      'new_review': 'info',
      'new_conversation': 'new_conversation',
      'new_message': 'info',
      'package_request': 'ride_request',
      'package_accepted': 'success',
      'package_declined': 'error',
      'package_cancelled': 'warning',
      'package_pickup': 'info',
      'package_delivered': 'info',
      'package_completed': 'success',
      'new_package_review': 'info'
    };

    const toastType = toastTypes[notificationData.type] || 'info';
    
    // 🌐 TRANSLATE the notification message to user's language
    let finalMessage = notificationData.message || 'You have a new notification';
    
    // If we have template and params, translate it (like NotificationItem.js does)
    if (notificationData.message_template && notificationData.message_params) {
      try {
        // Ensure template key is in correct format
        let translationKey = notificationData.message_template;
        if (translationKey.startsWith('notification.')) {
          translationKey = translationKey.replace('notification.', 'notifications.');
        }
        
        // Parse params if it's a string
        let params = notificationData.message_params;
        if (typeof params === 'string') {
          try {
            params = JSON.parse(params);
          } catch (e) {
            console.warn('Failed to parse message_params:', params);
            params = {};
          }
        }
        
        // Ensure we have the sender_name in params
        const translationParams = {
          ...params,
          sender_name: params.sender_name || notificationData.actor_name || params.sender || 'Someone'
        };
        
        console.log('🌐 Toast translating:', {
          key: translationKey,
          params: translationParams,
          userLang: i18n.language
        });
        
        // Translate in user's current language
        const translated = t(translationKey, {
          ...translationParams,
          lng: i18n.language, // Force current user language
          interpolation: { 
            escapeValue: false,
            prefix: '{{',
            suffix: '}}'
          }
        });
        
        // Check if translation worked (didn't just return the key)
        if (translated && translated !== translationKey && !translated.includes('{{')) {
          console.log('✅ Toast translation successful:', translated);
          finalMessage = translated;
        } else {
          console.warn('⚠️ Toast translation failed, using fallback');
        }
      } catch (error) {
        console.error('❌ Toast translation error:', error);
      }
    }
    
    const finalTitle = finalMessage; // Use the translated message for title
    
    // Create unique toast ID to prevent duplicates
    const uniqueId = `notification-${notificationData.id}`;
    
    console.log('✅ Toast creation (V2 with translation):', {
      id: uniqueId,
      type: toastType,
      title: finalTitle,
      description: finalMessage,
      wasTranslated: notificationData.message_template ? true : false
    });
    
    addToast({
      id: uniqueId,
      type: toastType,
      title: finalTitle,
      description: finalMessage
    });
  }, [addToast, i18n.language, t]);

  // Get notification title with translation (fallback only)
  const getNotificationTitle = useCallback((type, notificationData = null) => {
    // Simple fallback titles for any edge cases
    const titles = {
      'ride_request': 'New Ride Request',
      'ride_accepted': 'Ride Accepted',
      'ride_declined': 'Request Declined',
      'ride_cancelled': 'Ride Cancelled',
      'ride_completed': 'Ride Completed',
      'new_review': 'New Review',
      'new_conversation': 'New Conversation',
      'new_message': 'New Message',
      'package_request': 'Package Delivery Request',
      'package_accepted': 'Delivery Request Accepted',
      'package_declined': 'Delivery Request Declined',
      'package_cancelled': 'Delivery Cancelled',
      'package_pickup': 'Package Picked Up',
      'package_delivered': 'Package Delivered',
      'package_completed': 'Delivery Completed',
      'new_package_review': 'New Delivery Review'
    };
    return titles[type] || 'Notification';
  }, []);

  // Handle new notification by fetching it with full details and adding to list
  const handleNewNotification = useCallback(async (notificationId = null, createToast = false) => {
    if (!user?.id) return;

    try {
      console.log('New notification received, updating notification list');
      
      if (notificationId) {
        // Fetch the specific notification with actor details
        console.log('Fetching specific notification:', notificationId);
        const { data, error } = await supabase.rpc('get_user_notifications', {
          target_user_id: user.id,
          limit_count: 1,
          offset_count: 0,
          category_filter: null,
          unread_only: false
        });
        
        if (error) {
          console.error('Error fetching new notification:', error);
        } else if (data && data.length > 0) {
          const newNotification = data[0];
          console.log('Adding new notification to state:', newNotification);
          
          // Create toast from properly processed notification (with real usernames)
          if (createToast) {
            console.log('Creating toast from processed notification:', newNotification);
            createNotificationToast(newNotification);
          }
          
          dispatch({ 
            type: ACTION_TYPES.ADD_NOTIFICATION, 
            payload: newNotification 
          });
        }
      } else {
        // Fallback: refresh the entire list
        setTimeout(() => {
          console.log('Refreshing notification list for new notification');
          fetchNotifications();
        }, 500);
      }
    } catch (error) {
      console.error('Error handling new notification:', error);
      // Fallback: refresh the list
      setTimeout(() => {
        fetchNotifications();
      }, 500);
    }
  }, [user?.id, fetchNotifications, createNotificationToast]);

  // Toggle dropdown open/close
  const toggleDropdown = useCallback(() => {
    dispatch({ 
      type: ACTION_TYPES.SET_DROPDOWN_OPEN, 
      payload: !state.isDropdownOpen 
    });
  }, [state.isDropdownOpen]);

  // Close dropdown
  const closeDropdown = useCallback(() => {
    dispatch({ type: ACTION_TYPES.SET_DROPDOWN_OPEN, payload: false });
  }, []);

  // Create new notification (for testing or manual creation)
  const createNotification = useCallback(async (notificationData) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase.rpc('create_notification', {
        target_user_id: user.id,
        notification_type: notificationData.type,
        notification_category: notificationData.category,
        notification_message: notificationData.message,
        notification_data: notificationData.data || {},
        notification_actor_id: notificationData.actor_id || null,
        notification_related_id: notificationData.related_id || null,
        notification_action_url: notificationData.action_url || null,
        notification_priority: notificationData.priority || 'normal'
      });

      if (error) throw error;

      // Real-time subscription will handle adding to state
      return data;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }, [user?.id]);

  // Initialize real-time subscription when user changes
  useEffect(() => {
    console.log('NotificationProvider useEffect - user changed:', { 
      userId: user?.id, 
      userExists: !!user,
      isInitialized: isInitializedRef.current
    });
    
    if (!user?.id) {
      // Clear notifications when user logs out or not available
      dispatch({ type: ACTION_TYPES.SET_NOTIFICATIONS, payload: [] });
      dispatch({ type: ACTION_TYPES.SET_UNREAD_COUNT, payload: 0 });
      isInitializedRef.current = false;
      return;
    }

    // Always initialize when we have a user
    console.log('🚀 Initializing notifications for user:', user.id);
    isInitializedRef.current = true;
    
    // Add a small delay to ensure auth context is fully loaded
    const initTimer = setTimeout(() => {
      console.log('⏰ Delayed notification fetch starting...');
      fetchNotifications();
      fetchUnreadCount();
    }, 100);

    // Set up real-time subscription for notifications
    const notificationSubscription = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        handleRealtimeNotification
      )
      .subscribe();

    console.log('📡 Notification subscription created for user:', user.id);
    
    // Store cleanup function
    return () => {
      clearTimeout(initTimer);
      console.log('🧹 Cleaning up notification subscription for user:', user.id);
      notificationSubscription.unsubscribe();
      isInitializedRef.current = false;
    };
  }, [user?.id]); // Only depend on user.id to prevent loops

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (state.isDropdownOpen && !event.target.closest('.NTS-notification-bell-container')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [state.isDropdownOpen, closeDropdown]);

  // Context value
  const value = {
    // State
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    isLoading: state.isLoading,
    error: state.error,
    isDropdownOpen: state.isDropdownOpen,
    lastFetchTime: state.lastFetchTime,
    
    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    toggleDropdown,
    closeDropdown,
    createNotification,
    
    // Utilities
    refreshNotifications: () => {
      // Force refresh by directly calling internal function (bypass debouncing)
      console.log('🔄 refreshNotifications: Forcing immediate refresh');
      lastFetchRef.current = 0;
      fetchNotificationsInternal(10, 0, null);
    },
    
    // Emergency refresh with user ID (bypass context issues)
    emergencyRefresh: (userId) => {
      if (!userId) {
        console.log('❌ Emergency refresh: No user ID provided');
        return;
      }
      
      console.log('🆘 Emergency refresh triggered for user:', userId);
      
      // Direct Supabase call bypassing context user check
      supabase.rpc('get_user_notifications', {
        target_user_id: userId,
        limit_count: 10,
        offset_count: 0,
        category_filter: null,
        unread_only: false
      }).then(({ data, error }) => {
        if (error) {
          console.error('❌ Emergency refresh failed:', error);
          return;
        }
        
        console.log('✅ Emergency refresh successful:', data?.length || 0, 'notifications');
        dispatch({ type: ACTION_TYPES.SET_NOTIFICATIONS, payload: data || [] });
      });
      
      // Also refresh unread count
      supabase.rpc('get_unread_notification_count', {
        target_user_id: userId
      }).then(({ data, error }) => {
        if (!error) {
          dispatch({ type: ACTION_TYPES.SET_UNREAD_COUNT, payload: data || 0 });
        }
      });
    },
    
    clearError: () => dispatch({ type: ACTION_TYPES.SET_ERROR, payload: null })
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
